import React, { useState } from "react";
import { Button, FileInput, Grid, MultiSelect, NumberInput, Select, Switch, Textarea, TextInput, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconPhoto } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import useS3 from "../../../hooks/useS3";
import { renderFile } from "../../../utils/renderFile";

const STATE_OPTIONS = [
    { value: "Open", label: "Abierto (a la venta)" },
    { value: "Closed", label: "Cerrado" },
];

const DIVISION_OPTIONS = [
    { value: "SMALL", label: "Pequeños" },
    { value: "LARGE", label: "Grandes" },
    { value: "EXOTIC", label: "Exóticos" },
];

function ImageField({ form, path, label, folderName }) {
    const { uploadToS3 } = useS3();
    const [loading, setLoading] = useState(false);
    const value = path.split(".").reduce((acc, key) => acc?.[key], form.values);

    return (
        <>
            <FileInput
                label={label}
                placeholder={value ? "Reemplazar imagen" : "Cargar imagen"}
                leftSection={<IconPhoto size={24} />}
                accept={["image/*"]}
                disabled={loading}
                onChange={(file) => {
                    renderFile(file).then(async (fileBase64) => {
                        if (!fileBase64) return;
                        setLoading(true);
                        try {
                            const url = await uploadToS3({
                                image: fileBase64,
                                fileDirectory: "public/images/packs",
                                fileName: folderName,
                                isRequired: false,
                            });
                            if (url) form.setFieldValue(path, url);
                        } catch (error) {
                            showNotification({
                                color: "red",
                                title: "Ha ocurrido un problema al cargar el archivo.",
                                message: `Detalle: ${error}`,
                                autoClose: 12000,
                            });
                        } finally {
                            setLoading(false);
                        }
                    });
                }}
            />
            {value && <Text size="xs" c="dimmed" mt={4} style={{ wordBreak: "break-all" }}>{value}</Text>}
        </>
    );
}

function AcademicPackForm({ form, loading, academicUnitOptions }) {
    const hasStudents = (form.values.studentsCount || 0) > 0;
    const slug = (form.values.comercialInformation.name || "pack").toLowerCase().replace(/[^\w]+/g, "_");

    return (
        <>
            <Grid style={{ width: "100%" }}>
                <Grid.Col span={12}>
                    <MultiSelect
                        label="Unidades académicas incluidas"
                        description={hasStudents
                            ? `El pack ya tiene ${form.values.studentsCount} compradores: no se pueden cambiar sus cursos. Si hace falta, creá un pack nuevo y cerrá este.`
                            : "Cursos completos que recibe quien compra el pack (mínimo 2). Al comprarlo queda inscripto en cada uno."}
                        data={academicUnitOptions}
                        searchable
                        withAsterisk
                        disabled={hasStudents}
                        {...form.getInputProps("academicUnits")}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <TextInput label="Nombre" withAsterisk {...form.getInputProps("comercialInformation.name")} />
                </Grid.Col>
                <Grid.Col span={12}>
                    <Textarea label="Descripción" withAsterisk autosize minRows={3} {...form.getInputProps("comercialInformation.description")} />
                </Grid.Col>
                <Grid.Col span={12}>
                    <Textarea
                        label="¿Para quién es?"
                        description="Viene con el texto estándar. Podés modificarlo si este pack lo necesita."
                        autosize
                        minRows={3}
                        {...form.getInputProps("comercialInformation.whoIsThisFor")}
                    />
                </Grid.Col>

                <Grid.Col span={6}>
                    <NumberInput label="Precio en USD" withAsterisk min={0} {...form.getInputProps("comercialInformation.price.usd")} />
                </Grid.Col>
                <Grid.Col span={6}>
                    <NumberInput label="Precio en ARS" withAsterisk min={0} {...form.getInputProps("comercialInformation.price.ars")} />
                </Grid.Col>

                <Grid.Col span={6}>
                    <Select label="Estado" data={STATE_OPTIONS} allowDeselect={false} {...form.getInputProps("state")} />
                </Grid.Col>
                <Grid.Col span={6}>
                    <Select label="División" data={DIVISION_OPTIONS} allowDeselect={false} {...form.getInputProps("division")} />
                </Grid.Col>

                <Grid.Col span={6}>
                    <DateInput
                        label="Venta desde (opcional)"
                        clearable
                        valueFormat="DD/MM/YYYY"
                        value={form.values.comercialInformation.saleStartDateISO ? new Date(form.values.comercialInformation.saleStartDateISO) : null}
                        onChange={(date) => form.setFieldValue("comercialInformation.saleStartDateISO", date ? date.toISOString() : null)}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <DateInput
                        label="Venta hasta (opcional)"
                        clearable
                        valueFormat="DD/MM/YYYY"
                        value={form.values.comercialInformation.saleEndDateISO ? new Date(form.values.comercialInformation.saleEndDateISO) : null}
                        onChange={(date) => form.setFieldValue("comercialInformation.saleEndDateISO", date ? date.toISOString() : null)}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <ImageField form={form} path="comercialInformation.image" label="Imagen" folderName={slug} />
                </Grid.Col>
                <Grid.Col span={12}>
                    <TextInput label="Video (URL, opcional)" {...form.getInputProps("comercialInformation.video")} />
                </Grid.Col>
                <Grid.Col span={12}>
                    <Switch
                        label="Destacar en la web"
                        checked={!!form.values.comercialInformation.is_highlight}
                        onChange={(event) => form.setFieldValue("comercialInformation.is_highlight", event.currentTarget.checked)}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <Button type="submit" className="eventCalendarForm__form__button" loading={loading}>
                        Guardar pack
                    </Button>
                </Grid.Col>
            </Grid>
        </>
    );
}

export default AcademicPackForm;
